import { API_URLS } from '@helpers/api';
import useFocusWithin from '@hooks/useFocusWithin';
import { AnimatePresence } from 'framer-motion';
import * as React from 'react';
import { useLockBodyScroll } from 'react-use';
import S from './SearchField.styles';
const SearchField = ({ searchResultsPlaceholder, searchTypeaheadApi, searchResultsUrl, showSearchMobile, visibleCount = 9, onClose, }) => {
    const [searchTerm, setSearchTerm] = React.useState('');
    const items = useApi(searchTerm, searchTypeaheadApi);
    const refContainer = React.useRef(null);
    const refResultsContainer = React.useRef(null);
    const refInput = React.useRef(null);
    const showResults = useFocusWithin(refContainer);
    useLockBodyScroll(showResults && !!items.length);
    React.useEffect(() => {
        if (showSearchMobile) {
            refInput.current?.focus();
        }
    }, [showSearchMobile]);
    return (React.createElement(S.Container, { ref: refContainer, active: showSearchMobile, showResults: showResults && !!items.length, onKeyDown: (event) => {
            if (event.key.toLowerCase() === 'arrowdown') {
                handleArrowDown(event);
            }
            if (event.key.toLowerCase() === 'arrowup') {
                handleArrowUp(event);
            }
            if (event.key.toLowerCase() === 'escape') {
                handleEscape(event);
            }
            if (event.key.toLowerCase() === 'enter') {
                handleRedirect(event);
            }
        } },
        React.createElement(S.Inner, null,
            React.createElement(S.Input, { ref: refInput, placeholder: searchResultsPlaceholder, type: "search", value: searchTerm, onChange: (event) => {
                    setSearchTerm(event.target.value);
                } }),
            React.createElement(S.Submit, { type: "submit", onClick: handleRedirect },
                React.createElement(S.SubmitLabel, null, "Search"),
                React.createElement(S.SubmitIcon, null)),
            React.createElement(S.Close, { type: "button", onClick: (event) => {
                    event.preventDefault();
                    onClose();
                } },
                React.createElement(S.CloseLabel, null, "Close"),
                React.createElement(S.CloseIcon, null)),
            React.createElement(AnimatePresence, null, showResults && !!items?.length && (React.createElement(S.ResultsContainer, { ref: refResultsContainer, initial: 'hidden', animate: 'visible', exit: 'hidden', variants: {
                    hidden: {
                        opacity: 0,
                        transition: { type: 'tween', ease: 'easeOut' },
                    },
                    visible: {
                        opacity: 1,
                        transition: { type: 'tween', ease: 'easeOut' },
                    },
                } }, items?.slice(0, visibleCount).map((item) => (React.createElement(S.ResultItem, { key: item.url, href: item.url, tabIndex: -1 },
                React.createElement(S.ResultItemText, null, item.value))))))))));
    function handleRedirect(event) {
        event.preventDefault();
        if (!searchTerm || searchTerm.length === 0 || searchResultsUrl === null) {
            return;
        }
        window.location.href = `${searchResultsUrl}?search=${searchTerm}`;
    }
    function handleArrowDown(event) {
        event.preventDefault();
        if (showResults) {
            const resultSelected = refResultsContainer.current?.contains(document.activeElement);
            if (resultSelected) {
                const nextResult = document.activeElement?.nextElementSibling;
                if (nextResult) {
                    nextResult.focus();
                }
            }
            else {
                const firstResult = refResultsContainer.current?.firstChild;
                if (firstResult) {
                    firstResult.focus();
                }
            }
        }
    }
    function handleArrowUp(event) {
        event.preventDefault();
        if (showResults) {
            const resultSelected = refResultsContainer.current?.contains(document.activeElement);
            if (resultSelected) {
                const previousResult = document.activeElement?.previousElementSibling;
                if (previousResult) {
                    previousResult.focus();
                }
            }
        }
    }
    function handleEscape(event) {
        event.preventDefault();
        if (showResults) {
            const resultSelected = refResultsContainer.current?.contains(document.activeElement);
            if (resultSelected) {
                refInput.current?.focus();
            }
        }
    }
};
export default SearchField;
/** Use typeahead results for search term. */
function useApi(searchTerm, apiUrl) {
    const [data, setData] = React.useState([]);
    React.useEffect(() => {
        if (!searchTerm || searchTerm.length < 3) {
            return setData([]);
        }
        // Check for AbortController support (ie11).
        const abortController = window.AbortController ? new AbortController() : undefined;
        fetchApi(searchTerm, abortController?.signal, apiUrl).then((data) => setData(data));
        return function cancel() {
            abortController?.abort();
        };
    }, [searchTerm]);
    return data;
}
/** Call typeahead API endpoint. */
async function fetchApi(searchTerm, signal, apiUrl) {
    const res = await fetch(`${apiUrl ?? API_URLS.typeahead}?query=${searchTerm}`, { signal });
    if (!res.ok) {
        return [];
    }
    const data = await res.json();
    if (!data) {
        return [];
    }
    return data;
}
